import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import { videos } from '@/mocks/homeData';
import VideoCard from '@/pages/home/components/VideoCard';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videos.find((v) => v.id === id);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-error-warning-line text-2xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold text-foreground-950 font-heading mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该视频可能已被移除或链接错误</p>
            <Link to="/" className="px-6 py-2 bg-primary-500 text-white rounded-full text-sm font-medium hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedVideos = videos
    .filter((v) => v.categorySlug === video.categorySlug && v.id !== video.id)
    .slice(0, 4);

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16">
        <article>
          <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-4">
            <Breadcrumb items={[
              { label: '首页', href: '/' },
              { label: video.category, href: `/category/${video.categorySlug}` },
              { label: video.title },
            ]} />
          </div>

          <div className="max-w-[1400px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <div className="relative aspect-video rounded-xl overflow-hidden bg-foreground-950 mb-5">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/30">
                    <div className="w-16 h-16 md:w-20 md:h-20 flex items-center justify-center rounded-full bg-primary-500/90 text-white cursor-pointer hover:bg-primary-600 hover:scale-110 transition-all duration-300">
                      <i className="ri-play-fill text-2xl md:text-3xl"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-3 right-3 bg-foreground-950/80 text-white text-sm px-3 py-1 rounded-md">
                    {video.duration}
                  </div>
                </div>

                <h1 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-3">
                  {video.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-5 pb-5 border-b border-background-200/70">
                  <span className="flex items-center gap-1">
                    <i className="ri-user-line"></i>
                    {video.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {video.views} 次播放
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-star-fill text-amber-400"></i>
                    {video.rating}
                  </span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {video.date}
                  </span>
                </div>

                <div className="prose prose-sm max-w-none">
                  <h2 className="text-lg font-semibold text-foreground-950 mb-2 font-heading">视频简介</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">{video.description}</p>

                  <h2 className="text-lg font-semibold text-foreground-950 mb-2 font-heading">详细内容</h2>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    番茄视频为您精选的{video.category}内容，由{video.author}精心制作。本视频以高质量的画面和精彩的内容呈现，
                    带给观众极致的观看体验。视频涵盖{video.tags.join('、')}等多种元素，适合不同喜好的观众观看。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                    作为番茄视频平台的热门{video.category}内容，本视频自上线以来获得了广泛的关注和好评，
                    累计播放量达到{video.views}，评分高达{video.rating}分。这充分说明了内容的优质和观众的认可。
                  </p>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    番茄视频始终致力于为用户提供高品质的视频内容，聚合妖精视频、桃花视频、苹果视频、榴莲视频等特色频道，
                    动漫、短视频、搞笑视频、趣味节目一应俱全。高清画质免费在线观看，流畅不缓冲，让您尽情享受视频的乐趣。
                  </p>
                </div>

                <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-background-200/70">
                  {video.tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 bg-secondary-100 text-secondary-900 text-xs rounded-full">
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>

              <aside className="lg:col-span-1">
                <div className="sticky top-20">
                  <h3 className="text-base font-semibold text-foreground-950 font-heading mb-4">相关推荐</h3>
                  <div className="space-y-4">
                    {relatedVideos.map((rv) => (
                      <Link
                        key={rv.id}
                        to={`/article/${rv.id}`}
                        className="flex gap-3 group p-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
                      >
                        <div className="w-36 h-20 flex-shrink-0 rounded-lg overflow-hidden">
                          <img
                            src={rv.thumbnail}
                            alt={rv.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 group-hover:text-primary-500 transition-colors duration-200">
                            {rv.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
                            <span>{rv.views}</span>
                            <span>{rv.rating}分</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </article>

        <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <h2 className="text-xl font-bold text-foreground-950 font-heading mb-6">猜你喜欢</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {videos.filter(v => v.id !== video.id).slice(0, 4).map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}